import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';
import CurrencyInput from 'react-currency-input-field';
import { AxiosError, AxiosResponse } from 'axios';
import { modal } from '../modal/ModalEmitter';
import ApiService from '../../services/ApiService';
import { CardTransaction, Customer, Invoice, PaymentDepartment, PaymentMethod } from './types';
import toastr from '../toastr';
import NMIContainer from './NMIContainer';
import NMIPaymentForm from './NMIPaymentForm';

interface Props {
  customer: Customer;
  customerNum: string;
  department: PaymentDepartment;
  invoices: Invoice[];
  maxAmount: number;
  method: PaymentMethod;
  onSale: (paymentTransaction: CardTransaction) => void;
  refId: string;
  sessionId: string;
  showClose: boolean;
  terminalId?: string;
  userInitials: string;
}

const PaymentCardCharge = ({
  customer,
  customerNum,
  department,
  invoices,
  maxAmount,
  method,
  onSale,
  refId,
  sessionId = '',
  showClose = false,
  terminalId,
  userInitials,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(String(maxAmount));
  const [presetOption, setPresetOption] = useState('full');

  useEffect(() => {
    setAmount(String(maxAmount));
    setPresetOption('full');
  }, [maxAmount]);

  const handleAmountChange = (value: string | undefined = '0') => {
    if (value === amount) {
      return;
    }

    if (parseFloat(value) > maxAmount) {
      setAmount(maxAmount.toFixed(2));
    } else {
      setAmount(value || '0');
    }

    setPresetOption('custom');
  };

  const isAmountValid = () => {
    const value = parseFloat(amount);
    return value && value > 0 && value <= maxAmount;
  };

  const isFormValid = () => {
    if (!isAmountValid()) {
      return false;
    }

    if (method === 'terminal' && !terminalId) {
      return false;
    }

    return true;
  };

  const handleSale = () => {
    if (!userInitials) {
      toastr.error('Set User Initials');
      return;
    }

    setLoading(true);

    if (method === 'terminal') {
      // terminal id and amount needed
      return ApiService.post('/payment/terminal/transaction', {
        customer,
        customerNum,
        department,
        invoices,
        sessionId,
        amount,
        command: 'sale',
        refId,
        terminalId,
        userInitials,
      })
        .then((response: AxiosResponse) => {
          onSale(response.data.sale);
          setLoading(false);
        })
        .catch((err: AxiosError | Error) => {
          console.log(err);
          setLoading(false);
        });
    }

    if (method === 'cardNotPresent') {
      const cleanUp = () => {
        setLoading(false);
        modal.close();
      };

      const handleSuccess = (sale: CardTransaction) => {
        onSale(sale);
        cleanUp();
      };

      const handleError = (err: Error) => {
        console.error(err);
        cleanUp();
      };

      const handleDeclined = (sale: CardTransaction) => {
        onSale(sale);
        cleanUp();
      };

      modal.open('Payment', {
        title: 'Credit Card Payment',
        hidePrint: true,
        hideFooter: true,
        style: 'center',
        staticBackdrop: true,
        size: 'md',
        headerClass: 'primary',
        onClose: () => setLoading(false),
        message: (
          <NMIContainer>
            <NMIPaymentForm
              customerNum={customerNum}
              department={department}
              onSuccess={handleSuccess}
              onDeclined={handleDeclined}
              onError={handleError}
              values={{ amount }}
              invoices={invoices}
              sessionId={sessionId}
            />
          </NMIContainer>
        ),
      });
    }
  };

  const handlePresetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === 'full') {
      setAmount(String(maxAmount));
      setPresetOption('full');
    } else if (value === 'half') {
      setAmount((maxAmount / 2).toFixed(2));
      setPresetOption('half');
    } else {
      setPresetOption('custom');
    }
  };

  const renderActions = () => {
    if (loading) {
      return (
        <Button variant="default" disabled>
          <i className="fas fa-circle-notch fa-spin" /> &nbsp; Processing
        </Button>
      );
    }

    return (
      <Button disabled={!isFormValid()} onClick={handleSale}>
        Charge
      </Button>
    );
  };

  return (
    <Row>
      <Col xs="auto" className="form-select">
        <Form.Group className="mb-0">
          <Form.Control
            value={presetOption}
            onChange={handlePresetChange}
            as="select"
            className="d-inline mr-2"
            style={{ width: '100px' }}
          >
            <option value="full">Full</option>
            <option value="half">Half</option>
            <option value="custom">Custom</option>
          </Form.Control>
        </Form.Group>
      </Col>
      <Col>
        <Form.Group className="mb-0">
          <CurrencyInput
            className="form-control"
            decimalsLimit={2}
            decimalScale={2}
            defaultValue={amount}
            onValueChange={handleAmountChange}
            prefix="$"
            style={{ width: '80px', textAlign: 'right' }}
            value={amount}
          />
        </Form.Group>
      </Col>
      <Col xs="auto" className="text-right">
        {renderActions()}
      </Col>
    </Row>
  );
};

export default PaymentCardCharge;
